import { Component, HostListener, ViewChild, ElementRef, OnDestroy } from '@angular/core'
import { Router, NavigationEnd } from '@angular/router'
import { MatDialog } from '@angular/material/dialog'
import { trigger, transition, style, animate } from '@angular/animations'
import { Subject } from 'rxjs'
import { filter, takeUntil } from 'rxjs/operators'
import { AdminService } from '@services/admin.service'
import { AuthenticationService, ModuleType } from '@services/authentication.service'
import { NavPopupService } from '@services/nav-popup.service'
import { SubscriptionService } from '@services/subscription.service'
import { UserService } from '@services/user.service'
import { SearchModalComponent } from '../modals/search-modal/search-modal.component'

interface NavItem {
  label: string
  icon: string
  route: string
  module: ModuleType
  moduleId: string
  requiresAdmin?: boolean
  disabledInFreeTier?: boolean
  hideInFreeTier?: boolean
  hideForLegacy?: boolean
}

interface RecentItem extends NavItem {
  visitedTime: string
  timestamp: number
}

interface Tool {
  label: string
  icon: string
  action: string
}

@Component({
  selector: 'shared-nav-popup',
  templateUrl: './nav-popup.component.html',
  styleUrls: ['./nav-popup.component.css'],
  standalone: false,
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scaleY(0.95)', transformOrigin: 'top' }),
        animate('150ms cubic-bezier(0.16, 1, 0.3, 1)', style({ opacity: 1, transform: 'scaleY(1)' }))
      ])
    ])
  ]
})
export class NavPopupComponent implements OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>
  
  private destroy$ = new Subject<void>()
  
  isOpen = false
  searchQuery = ''
  filteredNavItems: NavItem[] = []
  filteredTools: Tool[] = []
  recentlyVisited: RecentItem[] = []

  get currentModule() { return this._authenticationService.currentModule }
  get hasUserAdminAccess() { return this._authenticationService.hasUserAdminAccess }
  get inFreeTier() { return this._subscriptionService.inFreeTier }
  get isLegacy() { return this._subscriptionService.isLegacy }
  get currentUser$() { return this._authenticationService.user$ }
  get isSuperAdmin() { return this._userService.currentUser?.superAdmin ?? false }
  get showAdminLink() { return this.isSuperAdmin }
  get showAISearch() { return !this._adminService.isModuleHidden('ai_search') }

  navItems: NavItem[] = [
    {
      label: 'Projects',
      icon: 'fa-layer-group',
      route: '/projects',
      module: 'projects',
      moduleId: 'projects'
    },
    {
      label: 'Models',
      icon: 'fa-cubes',
      route: '/models',
      module: 'models',
      moduleId: 'models'
    },
    {
      label: 'Assets',
      icon: 'fa-list-check',
      route: '/assets',
      module: 'assets',
      moduleId: 'assets',
      disabledInFreeTier: true
    },
    {
      label: 'OEE',
      icon: 'fa-gauge-high',
      route: '/oee',
      module: 'oee',
      moduleId: 'oee',
      disabledInFreeTier: true
    },
    {
      label: 'Organization',
      icon: 'fa-users-gear',
      route: '/organization',
      module: 'organization',
      moduleId: 'organization',
      requiresAdmin: true,
      disabledInFreeTier: true
    },
    {
      label: 'Billing',
      icon: 'fa-receipt',
      route: '/billing',
      module: 'billing',
      moduleId: 'billing',
      requiresAdmin: true,
      hideForLegacy: true
    },
    {
      label: 'My Shifts',
      icon: 'fa-clock',
      route: '/my-shifts',
      module: 'my-shifts' as ModuleType,
      moduleId: 'my-shifts',
      disabledInFreeTier: true
    }
  ]

  tools: Tool[] = [
    {
      label: 'AI Search',
      icon: 'fa-search',
      action: 'search'
    }
  ]

  constructor(
    private _adminService: AdminService,
    private _authenticationService: AuthenticationService,
    private _navPopupService: NavPopupService,
    private _router: Router,
    private _subscriptionService: SubscriptionService,
    private _userService: UserService,
    private _dialog: MatDialog
  ) {
    // Initialize filtered items
    this.filterItems()
    
    // Load recently visited from localStorage
    this.loadRecentlyVisited()
    
    // Track navigation for recently visited
    this._router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.trackVisit()
    })
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    this.close()
  }

  toggle(): void {
    this.isOpen = !this.isOpen
    if (this.isOpen) {
      this.searchQuery = ''
      this.filterItems()
      this._navPopupService.open()
      setTimeout(() => {
        if (this.searchInput) {
          this.searchInput.nativeElement.focus()
        }
      }, 150)
    } else {
      this._navPopupService.close()
    }
  }

  close(): void {
    this.isOpen = false
    this.searchQuery = ''
    this._navPopupService.close()
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.destroy$.next()
    this.destroy$.complete()
    
    // Ensure service state is cleaned up when component is destroyed
    this._navPopupService.close()
  }

  filterItems(): void {
    const query = this.searchQuery.toLowerCase().trim()
    
    if (!query) {
      this.filteredNavItems = [...this.navItems]
      this.filteredTools = [...this.tools]
    } else {
      this.filteredNavItems = this.navItems.filter(item => 
        item.label.toLowerCase().includes(query)
      )
      this.filteredTools = this.tools.filter(tool => 
        tool.label.toLowerCase().includes(query)
      )
    }
  }

  navigate(item: NavItem): void {
    if (this.isNavItemDisabled(item)) {
      return
    }
    this._router.navigate([item.route])
    this.addToRecentlyVisited(item)
    this.close()
  }

  isNavItemDisabled(item: NavItem): boolean {
    if (item.disabledInFreeTier && this.inFreeTier) {
      return true
    }
    return false
  }

  shouldShowNavItem(item: NavItem): boolean {
    if (item.hideForLegacy && this.isLegacy) {
      return false
    }
    if (item.hideInFreeTier && this.inFreeTier) {
      return false
    }
    if (item.requiresAdmin && !this.hasUserAdminAccess && !this.inFreeTier) {
      return false
    }
    if (this._adminService.isModuleHidden(item.moduleId)) {
      return false
    }
    return true
  }

  navigateToRoute(route: string): void {
    this._router.navigate([route])
    this.close()
  }

  openSearchModal(): void {
    this._dialog.open(SearchModalComponent, {
      width: '800px',
      maxWidth: '90vw',
      panelClass: 'search-modal-container'
    })
    this.close()
  }

  private trackVisit(): void {
    const currentItem = this.navItems.find(item => item.module === this.currentModule)
    if (currentItem) {
      this.addToRecentlyVisited(currentItem)
    }
  }

  private addToRecentlyVisited(item: NavItem): void {
    const now = Date.now()
    const recentItem: RecentItem = {
      ...item,
      visitedTime: this.getRelativeTime(now, now),
      timestamp: now
    }
    
    // Remove existing entry if present
    this.recentlyVisited = this.recentlyVisited.filter(r => r.route !== item.route)
    
    // Add to beginning
    this.recentlyVisited.unshift(recentItem)
    
    // Keep only last 5
    this.recentlyVisited = this.recentlyVisited.slice(0, 5)
    
    // Save to localStorage
    this.saveRecentlyVisited()
  }

  private loadRecentlyVisited(): void {
    const stored = localStorage.getItem('kartorium_recently_visited')
    if (stored) {
      try {
        const items: RecentItem[] = JSON.parse(stored)
        const now = Date.now()
        this.recentlyVisited = items
          .filter(item => item.route && item.label)
          .map(item => ({
            ...item,
            moduleId: item.moduleId || item.module || '',
            visitedTime: this.getRelativeTime(item.timestamp, now)
          }))
      } catch (e) {
        this.recentlyVisited = []
      }
    }
  }

  private saveRecentlyVisited(): void {
    localStorage.setItem('kartorium_recently_visited', JSON.stringify(this.recentlyVisited))
  }

  private getRelativeTime(timestamp: number, now: number): string {
    const seconds = Math.floor((now - timestamp) / 1000)
    
    if (seconds < 60) return 'Just now'
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`
    return 'Over a week ago'
  }
}
